import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "@/App.css";
import SEO from "../components/shared/SEO";
import { 
  ArrowLeft,
  BookOpen,
  Zap,
  Target,
  Users,
  CheckCircle2,
  ChevronRight,
  Lightbulb,
  Settings,
  BarChart3,
  RefreshCw,
  Award,
  AlertTriangle,
  Calendar,
  X,
  ChevronUp
} from "lucide-react";

// Brand Colors
const BRAND = {
  gold: "#D4AF37",
  goldLight: "#E8C547",
  charcoal: "#1C1C1C",
  surface: "#0A0A0A",
  text: "#FAFAFA"
};

// Section Data
const SECTIONS = [
  {
    id: "principle",
    title: "Core Principle",
    shortTitle: "Principle",
    icon: Lightbulb,
    content: [
      {
        type: "lead",
        text: "AI is infrastructure, not strategy. It automates top-of-funnel work—but it doesn't fix broken processes."
      },
      {
        type: "warning",
        title: "AI SDR does NOT fix pipeline problems",
        text: "AI automates top-of-funnel work like prospecting, outreach, follow-up, and qualification. If your system is broken, AI just scales the dysfunction faster."
      },
      {
        type: "insight",
        title: "The Real Differentiator",
        text: "Winning teams treat AI as infrastructure that supports human judgment—not a replacement for strategy, targeting, or relationship-building."
      },
      {
        type: "comparison",
        wrong: ["Start with tools", "Maximize volume", "Automate everything", "Measure activity"],
        right: ["Start with workflow", "Maximize signal", "Augment selectively", "Measure outcomes"]
      }
    ]
  },
  {
    id: "framework",
    title: "The Framework",
    shortTitle: "Framework",
    icon: Settings,
    content: [
      {
        type: "lead",
        text: "A four-stage workflow where AI augments each stage while humans retain judgment at key decision points."
      },
      {
        type: "workflow",
        steps: [
          { label: "Signal Input", sub: "Intent Data", icon: "BarChart3", color: "blue" },
          { label: "AI Processing", sub: "Enrichment + Draft", icon: "Settings", color: "purple" },
          { label: "SDR Decision", sub: "Approve / Edit", icon: "Users", color: "gold" },
          { label: "Execution", sub: "Send / Call", icon: "Zap", color: "green" }
        ]
      },
      {
        type: "insight",
        title: "Key Principle",
        text: "AI handles the repetitive work. Humans handle judgment calls. The magic is in knowing where to draw the line."
      }
    ]
  },
  {
    id: "signal",
    title: "Signal Over Volume",
    shortTitle: "Signal",
    icon: Target,
    content: [
      {
        type: "lead",
        text: "Quality signals beat activity metrics. Prioritize high-intent accounts and focus SDR time on the best opportunities."
      },
      {
        type: "numbered",
        items: [
          { title: "Intent Signals", desc: "Website visits, content downloads, competitor research, job postings" },
          { title: "Engagement Patterns", desc: "Email opens/clicks, ad interactions, social engagement" },
          { title: "Fit Indicators", desc: "Company size, tech stack, funding, growth signals" },
          { title: "Timing Triggers", desc: "Leadership changes, funding rounds, expansions" }
        ]
      },
      {
        type: "stat",
        value: "5x",
        label: "conversion rate improvement when prioritizing signal over volume"
      }
    ]
  },
  {
    id: "failures",
    title: "Failure Points",
    shortTitle: "Failures",
    icon: AlertTriangle,
    content: [
      {
        type: "lead",
        text: "Most AI SDR implementations fail for predictable reasons. Recognize these patterns early."
      },
      {
        type: "failureList",
        items: [
          { title: "Over-automation", desc: "Removing human touch from high-value interactions" },
          { title: "Poor targeting", desc: "AI blasts messages to everyone instead of best-fit accounts" },
          { title: "No feedback loop", desc: "System never learns what works" },
          { title: "Generic messaging", desc: "Templates that sound robotic and impersonal" },
          { title: "Measuring activity", desc: "Optimizing for emails sent instead of meetings booked" }
        ]
      }
    ]
  },
  {
    id: "human",
    title: "Human in the Loop",
    shortTitle: "Human",
    icon: Users,
    content: [
      {
        type: "lead",
        text: "The best systems balance AI efficiency with human judgment. Know where each adds the most value."
      },
      {
        type: "splitComparison",
        aiTasks: ["Data enrichment", "Initial research", "Draft creation", "Scheduling follow-ups", "Pattern recognition"],
        humanTasks: ["Account prioritization", "Message personalization", "Objection handling", "Relationship building", "Strategic decisions"]
      },
      {
        type: "insight",
        title: "The 80/20 Rule",
        text: "AI can handle 80% of the repetitive work. Humans should focus their energy on the 20% that actually moves deals forward."
      }
    ]
  },
  {
    id: "feedback",
    title: "Feedback Loops",
    shortTitle: "Feedback",
    icon: RefreshCw,
    content: [
      {
        type: "lead",
        text: "The best AI SDR systems are never 'done.' They're constantly learning from results and improving."
      },
      {
        type: "process",
        steps: ["Deploy", "Measure", "Learn", "Iterate"]
      },
      {
        type: "numbered",
        items: [
          { title: "Track Outcomes", desc: "Not just activity—meetings, pipeline, revenue" },
          { title: "A/B Test Constantly", desc: "Subject lines, templates, timing, targeting" },
          { title: "Review Weekly", desc: "What worked? What didn't? Why?" },
          { title: "Update Models", desc: "Feed learnings back into the system" }
        ]
      }
    ]
  },
  {
    id: "winning",
    title: "What Winners Do",
    shortTitle: "Winners",
    icon: Award,
    content: [
      {
        type: "lead",
        text: "The highest-performing teams follow a consistent playbook. Here's what separates them from the rest."
      },
      {
        type: "checklist",
        items: [
          "Start with workflow design, not tool selection",
          "Define clear human-AI handoff points",
          "Prioritize signal quality over volume",
          "Build feedback loops from day one",
          "Measure outcomes, not activity",
          "Iterate weekly based on results"
        ]
      }
    ]
  }
];

// Header Component
const Header = () => {
  const navigate = useNavigate();
  
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-14 sm:h-16">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <svg width="24" height="24" viewBox="0 0 64 64" fill="none">
              <rect x="6" y="46" width="11" height="14" fill={BRAND.gold} opacity="0.5" rx="2"/>
              <rect x="20" y="36" width="11" height="24" fill={BRAND.gold} opacity="0.65" rx="2"/>
              <rect x="34" y="24" width="11" height="36" fill={BRAND.gold} opacity="0.8" rx="2"/>
              <rect x="48" y="10" width="11" height="50" fill={BRAND.gold} rx="2"/>
            </svg>
            <span className="text-sm font-bold text-white">LRS</span>
          </button>
          
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </button>
        </div>
      </div>
    </header>
  );
};

// Sticky Section Navigation
const StickyNav = ({ sections, activeSection }) => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 120; // Account for sticky headers
      const y = element.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="sticky top-14 sm:top-16 z-40 bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center gap-1 py-2 overflow-x-auto scrollbar-hide">
          {sections.map((section, i) => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            return (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full whitespace-nowrap transition-all text-xs font-medium ${
                  isActive 
                    ? 'bg-[#D4AF37] text-[#0A0A0A]' 
                    : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{section.shortTitle}</span>
                <span className="sm:hidden">{i + 1}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Content Block Components
const LeadBlock = ({ text }) => (
  <p className="text-lg sm:text-xl text-zinc-200 leading-relaxed font-medium">
    {text}
  </p>
);

const WarningBlock = ({ title, text }) => (
  <div className="p-4 sm:p-5 bg-red-500/10 border-l-4 border-red-500 rounded-r-xl">
    <h4 className="text-base sm:text-lg font-semibold text-white mb-2">{title}</h4>
    <p className="text-sm text-zinc-300">{text}</p>
  </div>
);

const InsightBlock = ({ title, text }) => (
  <div className="p-4 sm:p-5 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl">
    <div className="flex items-start gap-3">
      <Lightbulb className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
      <div>
        <h4 className="font-semibold text-white mb-1">{title}</h4>
        <p className="text-sm text-zinc-300">{text}</p>
      </div>
    </div>
  </div>
);

const ComparisonBlock = ({ wrong, right }) => (
  <div className="grid sm:grid-cols-2 gap-4">
    <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
      <h4 className="text-sm font-semibold text-red-400 mb-3 flex items-center gap-2">
        <X className="w-4 h-4" />
        Wrong Approach
      </h4>
      <ul className="space-y-2">
        {wrong.map((item) => (
          <li key={item} className="flex items-center gap-2 text-sm text-zinc-300">
            <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
            {item}
          </li>
        ))}
      </ul>
    </div>
    <div className="p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-xl">
      <h4 className="text-sm font-semibold text-[#D4AF37] mb-3 flex items-center gap-2">
        <CheckCircle2 className="w-4 h-4" />
        Right Approach
      </h4>
      <ul className="space-y-2">
        {right.map((item) => (
          <li key={item} className="flex items-center gap-2 text-sm text-zinc-300">
            <CheckCircle2 className="w-3 h-3 text-[#D4AF37]" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const SplitComparisonBlock = ({ aiTasks, humanTasks }) => (
  <div className="grid sm:grid-cols-2 gap-4">
    <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl">
      <h4 className="text-sm font-semibold text-purple-400 mb-3 flex items-center gap-2">
        <Settings className="w-4 h-4" />
        AI Handles
      </h4>
      <ul className="space-y-2">
        {aiTasks.map((item) => (
          <li key={item} className="flex items-center gap-2 text-sm text-zinc-300">
            <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
            {item}
          </li>
        ))}
      </ul>
    </div>
    <div className="p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-xl">
      <h4 className="text-sm font-semibold text-[#D4AF37] mb-3 flex items-center gap-2">
        <Users className="w-4 h-4" />
        Humans Handle
      </h4>
      <ul className="space-y-2">
        {humanTasks.map((item) => (
          <li key={item} className="flex items-center gap-2 text-sm text-zinc-300">
            <CheckCircle2 className="w-3 h-3 text-[#D4AF37]" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const WorkflowBlock = ({ steps }) => {
  const iconMap = { BarChart3, Settings, Users, Zap };
  const colorMap = {
    blue: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    purple: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    gold: "bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30",
    green: "bg-green-500/20 text-green-400 border-green-500/30"
  };
  
  return (
    <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
      <div className="flex gap-2 sm:gap-3 min-w-max pb-2">
        {steps.map((step, i) => {
          const Icon = iconMap[step.icon];
          return (
            <div key={step.label} className="flex items-center">
              <div className={`p-3 sm:p-4 rounded-xl ${colorMap[step.color]} border min-w-[100px] sm:min-w-[120px] text-center`}>
                <Icon className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-2" />
                <div className="font-semibold text-white text-xs sm:text-sm">{step.label}</div>
                <div className="text-[10px] sm:text-xs text-zinc-400 mt-1">{step.sub}</div>
              </div>
              {i < steps.length - 1 && (
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-600 mx-1" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const NumberedBlock = ({ items }) => (
  <div className="space-y-3">
    {items.map((item, i) => (
      <div key={item.title} className="flex gap-3 p-3 sm:p-4 bg-[#1C1C1C] rounded-xl border border-white/5">
        <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-bold text-[#0A0A0A]">{i + 1}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-white text-sm sm:text-base">{item.title}</h4>
          <p className="text-xs sm:text-sm text-zinc-400 mt-0.5">{item.desc}</p>
        </div>
      </div>
    ))}
  </div>
);

const StatBlock = ({ value, label }) => (
  <div className="p-6 bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 rounded-2xl border border-[#D4AF37]/30 text-center">
    <div className="text-4xl sm:text-5xl font-bold text-[#D4AF37] mb-2">{value}</div>
    <div className="text-sm text-zinc-300">{label}</div>
  </div>
);

const FailureListBlock = ({ items }) => (
  <div className="space-y-3">
    {items.map((item) => (
      <div key={item.title} className="p-3 sm:p-4 bg-red-500/5 border border-red-500/20 rounded-xl">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <h5 className="font-medium text-white text-sm">{item.title}</h5>
            <p className="text-xs text-zinc-400 mt-0.5">{item.desc}</p>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const ProcessBlock = ({ steps }) => (
  <div className="p-5 bg-[#1C1C1C] rounded-2xl border border-white/5">
    <div className="flex justify-between items-center">
      {steps.map((step, i) => (
        <div key={step} className="flex-1 text-center relative">
          <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-2 ${
            i === 0 ? 'bg-[#D4AF37] text-[#0A0A0A]' : 'bg-white/10 text-white'
          }`}>
            {i + 1}
          </div>
          <div className="text-xs text-zinc-400">{step}</div>
          {i < steps.length - 1 && (
            <div className="absolute top-5 left-[60%] w-[80%] h-px bg-white/10" />
          )}
        </div>
      ))}
    </div>
  </div>
);

const ChecklistBlock = ({ items }) => (
  <div className="p-5 bg-[#1C1C1C] rounded-2xl border border-white/5">
    <div className="space-y-3">
      {items.map((item) => (
        <div key={item} className="flex items-start gap-3">
          <div className="w-5 h-5 rounded border-2 border-[#D4AF37] flex items-center justify-center flex-shrink-0 mt-0.5">
            <CheckCircle2 className="w-3 h-3 text-[#D4AF37]" />
          </div>
          <span className="text-sm text-zinc-300">{item}</span>
        </div>
      ))}
    </div>
  </div>
);

const CTABlock = () => (
  <div className="p-6 bg-gradient-to-br from-[#D4AF37]/20 to-[#1C1C1C] rounded-2xl border border-[#D4AF37]/30 text-center">
    <h4 className="text-lg font-semibold text-white mb-2">Ready to Build Your System?</h4>
    <p className="text-sm text-zinc-300 mb-4">Let's discuss how these frameworks can be applied to your specific situation.</p>
    <a 
      href="https://calendly.com/leitner-revenue-systems/30min"
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#0A0A0A] rounded-xl font-semibold text-sm hover:bg-[#E8C547] transition-all"
    >
      <Calendar className="w-4 h-4" />
      Book a Strategy Call
    </a>
  </div>
);

// Section Component
const Section = ({ section, index, isVisible }) => {
  const Icon = section.icon;
  
  const renderContent = (content, idx) => {
    switch (content.type) {
      case 'lead': return <LeadBlock key={idx} text={content.text} />;
      case 'warning': return <WarningBlock key={idx} title={content.title} text={content.text} />;
      case 'insight': return <InsightBlock key={idx} title={content.title} text={content.text} />;
      case 'comparison': return <ComparisonBlock key={idx} wrong={content.wrong} right={content.right} />;
      case 'splitComparison': return <SplitComparisonBlock key={idx} aiTasks={content.aiTasks} humanTasks={content.humanTasks} />;
      case 'workflow': return <WorkflowBlock key={idx} steps={content.steps} />;
      case 'numbered': return <NumberedBlock key={idx} items={content.items} />;
      case 'stat': return <StatBlock key={idx} value={content.value} label={content.label} />;
      case 'failureList': return <FailureListBlock key={idx} items={content.items} />;
      case 'process': return <ProcessBlock key={idx} steps={content.steps} />;
      case 'checklist': return <ChecklistBlock key={idx} items={content.items} />;
      case 'cta': return <CTABlock key={idx} />;
      default: return null;
    }
  };

  return (
    <section 
      id={section.id}
      className={`transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Section Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-[#D4AF37] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#D4AF37]/20">
          <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-[#0A0A0A]" />
        </div>
        <div>
          <span className="text-xs font-medium text-[#D4AF37]">Section {index + 1}</span>
          <h2 className="text-xl sm:text-2xl font-bold text-white">{section.title}</h2>
        </div>
      </div>

      {/* Section Content */}
      <div className="space-y-5 pl-0 sm:pl-[72px]">
        {section.content.map((content, idx) => renderContent(content, idx))}
      </div>

      {/* Section Divider */}
      <div className="mt-10 mb-10 border-t border-white/5" />
    </section>
  );
};

// Back to Top Button
const BackToTop = ({ show }) => (
  <button
    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
    className={`fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-[#D4AF37] text-[#0A0A0A] shadow-lg shadow-[#D4AF37]/30 flex items-center justify-center transition-all duration-300 ${
      show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
    }`}
  >
    <ChevronUp className="w-6 h-6" />
  </button>
);

// Main Component
function GuidebookPage() {
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const sectionRefs = useRef({});

  useEffect(() => {
    // Trigger fade-in animation
    setIsLoaded(true);

    const handleScroll = () => {
      // Update back to top button visibility
      setShowBackToTop(window.scrollY > 400);

      // Update active section in nav
      const scrollPosition = window.scrollY + 150;
      
      for (let i = SECTIONS.length - 1; i >= 0; i--) {
        const section = SECTIONS[i];
        const element = document.getElementById(section.id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
    // SECTIONS is a static constant defined outside component
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] overflow-x-hidden">
      <SEO 
        title="AI-Powered SDR Team Design Guidebook"
        description="Designing AI SDR systems that scale without losing the human edge. Learn how top teams treat AI as infrastructure, not a shortcut."
        path="/resources/ai-sdr-guidebook"
        image="resources"
      />
      <Header />
      <StickyNav sections={SECTIONS} activeSection={activeSection} />
      
      {/* Hero */}
      <section className="pt-28 sm:pt-32 pb-8 sm:pb-10 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/10 via-transparent to-transparent" />
          <img 
            src="https://media.licdn.com/dms/image/v2/D5612AQHoqD9j6OPvfQ/article-cover_image-shrink_720_1280/B56ZuaiYovKEAI-/0/1767824267682?e=2147483647&v=beta&t=ruFDrcPhUkjhXWmtJ0Syx8_TeZX8JTAYko63S-061Lw"
            alt="AI SDR System"
            className="absolute inset-0 w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/90 to-[#0A0A0A]/70" />
        </div>
        
        <div className="relative max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs font-medium text-[#D4AF37]">LRS Playbook</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight mb-4">
            AI-Powered SDR{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#E8C547]">
              Design Guidebook
            </span>
          </h1>
          
          <p className="text-base sm:text-lg text-zinc-300 mb-6 max-w-2xl">
            Learn how the highest-performing teams design AI systems that scale without losing the human edge.
          </p>
          
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_efficiency-boost-10/artifacts/tvktt6ia_IMG_0009.jpeg"
              alt="Dan Leitner"
              className="w-12 h-12 rounded-full object-cover border-2 border-[#D4AF37]/30"
            />
            <div>
              <div className="font-medium text-white">Dan Leitner</div>
              <div className="text-sm text-[#D4AF37]">Founder & Principal Consultant</div>
            </div>
          </div>
        </div>
      </section>

      {/* All Sections - Open by Default */}
      <div className="px-4 sm:px-6 lg:px-12 pb-20">
        <div className="max-w-4xl mx-auto">
          {SECTIONS.map((section, index) => (
            <Section
              key={section.id}
              section={section}
              index={index}
              isVisible={isLoaded}
            />
          ))}
        </div>
      </div>

      {/* Final CTA */}
      <div className="px-4 sm:px-6 pb-20">
        <div className="max-w-4xl mx-auto p-8 bg-gradient-to-br from-[#D4AF37]/20 to-[#1C1C1C] rounded-2xl border border-[#D4AF37]/30 text-center">
          <h3 className="text-2xl font-bold text-white mb-3">Ready to Build Your System?</h3>
          <p className="text-zinc-300 mb-6 max-w-md mx-auto">
            Let's discuss how these frameworks can be applied to your specific revenue challenges.
          </p>
          <a 
            href="https://calendly.com/leitner-revenue-systems/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-[#0A0A0A] rounded-xl font-semibold hover:bg-[#E8C547] transition-all shadow-lg shadow-[#D4AF37]/20"
          >
            <Calendar className="w-5 h-5" />
            Book a Strategy Call
          </a>
        </div>
      </div>

      <BackToTop show={showBackToTop} />
    </div>
  );
}

export default GuidebookPage;
